import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  Factory,
  Package,
  Layers,
  Database,
  CheckCircle,
  ArrowRight,
  Network,
  Truck,
  Users,
  ChevronDown,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import Breadcrumbs from '@/components/Breadcrumbs';
import SEO from '@/components/SEO';
import ROIResultsCapture from '@/components/ROIResultsCapture';
import { Link } from 'react-router-dom';

const Manufacturing = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [roiInputs, setRoiInputs] = useState({
    invoices: 2000,
    avgCost: 15,
    exceptionRate: 35,
    staffRate: 28,
    plantCount: 3,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentCost = roiInputs.invoices * roiInputs.avgCost;
  const hoursSpentPerMonth = roiInputs.invoices * 0.545;
  const exceptionsHandledManually = Math.round((roiInputs.invoices * roiInputs.exceptionRate) / 100);
  const coordinationCost = roiInputs.plantCount * 15 * roiInputs.staffRate;
  const processingTimeReduction = 0.75;
  const newProcessingCost = currentCost * (1 - processingTimeReduction);
  const processingCostSavings = currentCost - newProcessingCost;
  const hoursSaved = hoursSpentPerMonth * 0.75;
  const exceptionsSaved = Math.round(exceptionsHandledManually * 0.9);
  const exceptionResolutionSavings = (exceptionsSaved * 20) / 60 * roiInputs.staffRate;
  const laborSavings = hoursSaved * roiInputs.staffRate;
  const totalMonthlySavings = laborSavings + exceptionResolutionSavings + coordinationCost + processingCostSavings;
  const ezCloudCost = 3000;
  const netSavings = totalMonthlySavings - ezCloudCost;
  const annualSavings = netSavings * 12;
  const roi = (annualSavings / (ezCloudCost * 12)) * 100;
  const paybackMonths = ezCloudCost / netSavings;

  const painPoints = [
    {
      icon: <Package className="w-6 h-6 text-accent" />,
      title: 'Invoice volume is high and PO-backed',
      body: "Manufacturers run thousands of POs per month. Each produces at least one invoice, often several (partial shipments, backorders, credits). Generic AP tools add human coding steps that shouldn't be needed on PO-backed invoices — EZ Cloud matches header and line detail against the PO and posts clean matches without human touch.",
    },
    {
      icon: <Layers className="w-6 h-6 text-accent" />,
      title: 'GRN matching is the actual bottleneck',
      body: "Most exceptions come from GRN variance, not price or quantity. Receiving departments enter partial receipts late; invoices arrive before GRN posts; GRN quantities don't match PO expectations. EZ Cloud surfaces GRN variance as the specific exception type, routes it to receiving (not AP), and holds the invoice until GRN is resolved — without blocking everything else.",
    },
    {
      icon: <Database className="w-6 h-6 text-accent" />,
      title: 'ERP fit is the whole game',
      body: "A manufacturer on SAP S/4HANA, Oracle EBS, or Infor LN needs posting that respects the ERP's master data (cost centers, profit centers, plants, storage locations, WBS elements). Middleware AP tools flatten these to a GL code. EZ Cloud posts into the ERP's full dimensional structure.",
    },
    {
      icon: <Network className="w-6 h-6 text-accent" />,
      title: 'Intercompany between plants and entities',
      body: 'Multi-plant manufacturers post intercompany invoices constantly. Generic tools handle this by kicking the intercompany leg to a separate manual posting. EZ Cloud handles intercompany at the point of coding.',
    },
    {
      icon: <Truck className="w-6 h-6 text-accent" />,
      title: 'Freight, duty, and landed-cost invoices need special handling',
      body: 'Import-heavy manufacturers deal with freight forwarders, brokers, customs duty, and landed-cost allocation. EZ Cloud supports landed-cost invoices posting against PO receipts, with the allocation logic the ERP expects.',
    },
    {
      icon: <Users className="w-6 h-6 text-accent" />,
      title: 'Supplier master in the ERP is the source of truth',
      body: 'Plant purchasing creates suppliers in the ERP. AP tools that maintain their own supplier list drift immediately. EZ Cloud pulls suppliers directly from the ERP master — no duplicate list, no drift.',
    },
  ];

  const howItWorks = [
    { title: 'Native 3-way match against the ERP', body: 'PO, GRN, and invoice matched at header and line.' },
    { title: 'GRN variance as a named exception type', body: 'Routed to receiving, not AP.' },
    { title: 'Dimensional posting', body: 'Cost center, profit center, plant, storage location, WBS — posted with full specificity.' },
    { title: 'Intercompany at coding', body: 'Complete intercompany entry on post.' },
    { title: 'Supplier master from the ERP', body: 'No duplicate supplier list, no drift.' },
    { title: 'Integrations for SAP, Oracle, NetSuite, Dynamics', body: 'SAP S/4HANA, Oracle EBS, Oracle Fusion, NetSuite, Dynamics 365 F&O — all native.' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [] as Array<{
      '@type': 'Question';
      name: string;
      acceptedAnswer: { '@type': 'Answer'; text: string };
    }>,
  };

  const faqs = [
    {
      question: 'How does 3-way match work?',
      answer:
        'Invoices are matched to the PO and the GRN by vendor, PO number, line, quantity, and unit price. Configurable tolerance thresholds determine auto-approval. Variances outside tolerance route to named exception handlers — GRN variance goes to receiving, price variance to buyer, quantity variance to AP.',
    },
    {
      question: 'What ERPs does EZ Cloud integrate with for manufacturing?',
      answer:
        'SAP S/4HANA, SAP ECC, Oracle E-Business Suite, Oracle Fusion Cloud, NetSuite, Microsoft Dynamics 365 F&O, Infor LN and CloudSuite, and Epicor. Each integration is native.',
    },
    {
      question: "Can EZ Cloud post into SAP's full dimensional structure?",
      answer:
        'Yes. Cost center, profit center, plant, storage location, WBS element, and internal order are posted with the invoice. No dimension flattening.',
    },
    {
      question: 'How are GRN variances handled?',
      answer:
        "GRN variance is a named exception type. Invoices with GRN variance hold at the invoice level but don't block unrelated invoices. The exception routes to the configured receiving approver. Once GRN is corrected in the ERP, the invoice releases automatically.",
    },
    {
      question: 'Does EZ Cloud handle landed-cost invoices?',
      answer:
        'Landed-cost invoices from freight forwarders, brokers, and customs post against PO receipts with the allocation logic the ERP supports.',
    },
    {
      question: 'How is the supplier master kept in sync with the ERP?',
      answer:
        'EZ Cloud reads the supplier master directly from the ERP on each invoice. No duplicate supplier list is maintained in EZ Cloud. Suppliers added in the ERP are immediately available in EZ Cloud.',
    },
    {
      question: "What's implementation like for a mid-market manufacturer?",
      answer:
        'Typical timeline is 10–14 weeks depending on ERP complexity. Phases: ERP integration setup (3–4 weeks), master data and PO migration (2 weeks), tolerance and workflow configuration (2–3 weeks), UAT (2 weeks), parallel run and cut-over (2 weeks).',
    },
    {
      question: 'What about multi-plant rollout — do we need to roll out plant by plant?',
      answer:
        'No, though phased rollouts by plant are common and recommended. Most customers start with one plant, prove the integration, then extend.',
    },
    {
      question: 'How does intercompany work?',
      answer:
        'Intercompany splits are handled at coding. The base invoice and the intercompany leg post together; no manual intercompany posting after the fact.',
    },
    {
      question: "What's the security posture?",
      answer:
        'SOC 2 Type II certified. Encryption in transit (TLS 1.2+) and at rest (AES-256). Role-based access. SSO via SAML 2.0 and OIDC.',
    },
  ];

  faqSchema.mainEntity = faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  }));

  return (
    <div className="min-h-screen">
      <SEO
        title="AP Automation for Manufacturers | 3-Way Match + GRN | EZ Cloud"
        description="High-volume invoice throughput, GRN-to-invoice-to-PO matching, and native posting into SAP, Oracle, NetSuite, and Dynamics — prebuilt integrations maintained by EZ Cloud."
        canonical="https://ezcloud.ai/industries/manufacturing"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Industries', url: 'https://ezcloud.ai/#industries' },
          { name: 'Manufacturing', url: 'https://ezcloud.ai/industries/manufacturing' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* HERO */}
      <section className="bg-hero-gradient text-white min-h-[80vh] relative overflow-hidden flex items-center">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto px-4 relative py-20">
          <div className="max-w-4xl space-y-8">
            <Badge className="bg-accent/20 text-accent border-accent/30 px-4 py-2 text-sm">
              <Factory className="w-4 h-4 mr-2" />
              Built for Manufacturers
            </Badge>

            <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
              AP automation for manufacturers,
              <span className="text-accent"> built around 3-way match and GRN</span>
            </h1>

            <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl">
              High-volume invoice throughput, GRN-to-invoice-to-PO matching, and native posting into the ERP that runs your plant — SAP S/4HANA, Oracle EBS, NetSuite, Dynamics 365, Infor. Each has a prebuilt, EZ Cloud-maintained integration.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book a manufacturing AP demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline-on-dark"
                className="font-semibold px-8 py-6 text-lg"
                asChild
              >
                <Link to="/integrations">See supported ERPs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              AP problems specific to manufacturers
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {painPoints.map((p, i) => (
              <Card key={i} className="border border-gray-200 bg-white h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      {p.icon}
                    </div>
                    <CardTitle className="text-xl text-gray-900 leading-snug">
                      {i + 1}. {p.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                How it works for manufacturers
              </h2>
            </div>

            <div className="space-y-4">
              {howItWorks.map((step, i) => (
                <div key={i} className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-gray-700">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold mb-4">Estimate your savings</h2>
              <p className="text-lg text-white/90">
                Model the impact of EZ Cloud against your current AP cost structure.
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Monthly invoice volume:{' '}
                        <span className="text-primary">{roiInputs.invoices.toLocaleString()}</span>
                      </label>
                      <input
                        type="range"
                        min="500"
                        max="10000"
                        step="250"
                        value={roiInputs.invoices}
                        onChange={(e) => setRoiInputs({ ...roiInputs, invoices: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Current cost per invoice:{' '}
                        <span className="text-primary">${roiInputs.avgCost}</span>
                      </label>
                      <input
                        type="range"
                        min="5"
                        max="30"
                        step="1"
                        value={roiInputs.avgCost}
                        onChange={(e) => setRoiInputs({ ...roiInputs, avgCost: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Exception rate:{' '}
                        <span className="text-primary">{roiInputs.exceptionRate}%</span>
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="50"
                        step="5"
                        value={roiInputs.exceptionRate}
                        onChange={(e) => setRoiInputs({ ...roiInputs, exceptionRate: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Average staff hourly rate:{' '}
                        <span className="text-primary">${roiInputs.staffRate}</span>
                      </label>
                      <input
                        type="range"
                        min="15"
                        max="50"
                        step="1"
                        value={roiInputs.staffRate}
                        onChange={(e) => setRoiInputs({ ...roiInputs, staffRate: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Plants: <span className="text-primary">{roiInputs.plantCount}</span>
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="25"
                        step="1"
                        value={roiInputs.plantCount}
                        onChange={(e) => setRoiInputs({ ...roiInputs, plantCount: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Net monthly savings</div>
                      <div className="text-4xl font-bold text-primary">
                        ${netSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="border-t border-gray-300 pt-4">
                      <div className="text-sm text-gray-600 mb-1">Annual savings</div>
                      <div className="text-3xl font-bold text-accent">
                        ${annualSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 border-t border-gray-300 pt-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-1">ROI</div>
                        <div className="text-2xl font-bold text-green-600">{roi.toFixed(0)}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-1">Payback</div>
                        <div className="text-2xl font-bold text-blue-600">
                          {paybackMonths.toFixed(1)} mo
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 pt-2 border-t border-gray-300">
                      Estimates. Model uses 88–92% touchless processing and up to 90% email-exception prevention as a baseline. Individual results vary by ERP, invoice mix, and process maturity.
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <ROIResultsCapture
                    industry="Manufacturing"
                    annualSavings={annualSavings}
                    inputs={{
                      'Monthly invoices': roiInputs.invoices.toLocaleString(),
                      'Avg cost per invoice': `$${roiInputs.avgCost}`,
                      'Exception rate': `${roiInputs.exceptionRate}%`,
                      Plants: roiInputs.plantCount,
                      'Staff hourly rate': `$${roiInputs.staffRate}`,
                    }}
                    buttonLabel="Email my manufacturing ROI report"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* RELATED RESOURCES */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                Related EZ Cloud resources for manufacturing AP
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Manufacturing-relevant ERPs, adjacent verticals, and the integration technology that makes high-volume 3-way match work.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/integrations/sap"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  SAP ECC & S/4HANA
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  MIRO replacement, multi-plant routing, dimensional posting (cost center, profit center, plant, storage location, WBS).
                </p>
              </Link>

              <Link
                to="/integrations/oracle"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Oracle EBS & Fusion
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Native AP integration with Oracle E-Business Suite and Oracle Fusion Cloud — multi-BU, project, and intercompany routing built in.
                </p>
              </Link>

              <Link
                to="/integrations/dynamics"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  Dynamics 365 F&O
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Financial-dimension coding, multi-legal-entity routing, manufacturer-friendly Power Platform integration.
                </p>
              </Link>

              <Link
                to="/integrations/netsuite"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  NetSuite
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Mid-market manufacturer fit — vendor bill posting, OneWorld subsidiary routing, SuiteFlow-aware approvals.
                </p>
              </Link>

              <Link
                to="/technology/native-erp"
                className="block p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all bg-white"
              >
                <div className="flex items-center gap-2 text-primary font-semibold mb-1">
                  How native ERP integration works
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </div>
                <p className="text-sm text-gray-600">
                  Real-time APIs, zero-downtime deployment, SOC 2 compliance — the architecture behind every EZ Cloud manufacturing deployment.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <Card
                  key={index}
                  className="border border-gray-200 bg-white cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <CardTitle className="text-lg text-left text-gray-900 font-semibold">
                        {faq.question}
                      </CardTitle>
                      <ChevronDown
                        className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </CardHeader>
                  {openFaq === index && (
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Ready to see EZ Cloud on your ERP?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Book a 30-minute discovery call and we'll walk through the integration against your setup.
            </p>
            <div className="flex justify-center">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-lg"
                onClick={() => openCalendlyDemo()}
              >
                Book a manufacturing AP demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;
